import React, { HTMLAttributes } from 'react';
import s from './loader.scss';
import { cn } from '@express-labs/raven-tools';

export interface LoaderProps extends HTMLAttributes<HTMLSpanElement> {
  /** Changes the appearance of the Loader.  A chroma of "dark" helps the Loader stand out on dark backgrounds.  "light" is good for light backgrounds. */
  chroma?: 'dark' | 'light';
  /** Optional custom CSS to append to the container span HTML element. */
  className?: string;
  /** Optional custom CSS to append to the internal spinner span HTML element */
  classNameSpinner?: string;
  /** Makes the Loader component inline, block, or a fixed overlay */
  display?: 'inline' | 'block' | 'overlay';
  /** The "key" prop is a <a href="https://kentcdodds.com/blog/understanding-reacts-key-prop">special React property</a> that, when defined, can trigger a re-render of the component */
  key?: string;
  /** Changes the size of the spinner to one of four pre-configured sizes */
  size?: 'mini' | 'small' | 'medium' | 'large';
}

/**
 * The Loader component is a spinning busy indicator useful for alerting the user that they are
 * waiting for an asynchronous event to finish before they can continue.  In addition to the
 * properties listed in Storybook, you can add any property that's valid for an HTML span element.
 * They will be applied to the main span HTML element that wraps the spinner.
 */
const Loader = ({
  chroma = 'dark',
  className,
  classNameSpinner,
  display = 'block',
  key,
  size = 'small',
  ...filteredProps
}: LoaderProps) => (
  <span
    data-testid="container"
    className={cn('loader', s.container, s[display], s[size], className)}
    key={key}
    {...filteredProps}
  >
    <span
      data-testid="spinner"
      className={cn(
        'loader__spinner',
        s.loading,
        s[size],
        s[chroma],
        classNameSpinner
      )}
    />
  </span>
);

Loader.displayName = 'Loader';

export default Loader;
